<template>
  <div class="cross-section-charts-wrapper">
    <div v-if="hasData">
      <CrossSectionChartsGroup
        v-if="displaySet === 'top' || displaySet === 'both'"
        :title="displaySet"
        :cross-section-data="crossSectionData.topEquipmentData"
        :small-length-unit="smallLengthUnit"
      />
      <CrossSectionChartsGroup
        v-if="
          (displaySet === 'bottom' || displaySet === 'both') &&
          hasBottomEquipmentData &&
          crossSectionData?.bottomEquipmentData
        "
        :title="displaySet"
        :cross-section-data="crossSectionData.bottomEquipmentData"
        :small-length-unit="smallLengthUnit"
      />
    </div>

    <div v-else class="no-data-message">
      No cross-section data available. Please check equipment configuration.
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import CrossSectionChartsGroup from "./CrossSectionChartsGroup.vue";
import { useCrossSectionChartingData } from "@/libs/charting/crossSection";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import {
  scenarioCalculationsKey,
  scenarioKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import type { Scenario } from "@/composables/forms/formTypes";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";

const props = defineProps<{
  title?: string;
  scenarioCalculations?: ScenarioCalculations;
  scenario?: Scenario | null;
  equipmentSet?: "top" | "bottom" | "both";
}>();

const _calcs = props.scenarioCalculations
  ? props.scenarioCalculations
  : injectStrict(scenarioCalculationsKey);

// const upperCalcs = useStackCalculations("upper") as ScenarioCalculations;
// const lowerCalcs = useStackCalculations("lower") as ScenarioCalculations;
const _scenario = props.scenario || injectStrict(scenarioKey);
const _sizing = injectStrict(sizingKey, null);

const { getSmallLengthUnit } = useScenarioUnits(_scenario, _sizing);
const smallLengthUnit = getSmallLengthUnit();

const displaySet = computed(() => {
  return props.equipmentSet ? props.equipmentSet : "both";
});

// Get cross-section data using the hook
const crossSectionData = computed(() => {
  return useCrossSectionChartingData(_calcs, smallLengthUnit.value);
});

// Check if we have any data to display
const hasData = computed(() => {
  if (!crossSectionData.value) return false;
  // Only need to worry about top equipment since you can't have bottom without top
  return !!(
    (crossSectionData.value.topEquipmentData.tubingChartSet &&
      crossSectionData.value.topEquipmentData.tubingChartSet.length > 0) ||
    (crossSectionData.value.topEquipmentData.pumpChartSet &&
      crossSectionData.value.topEquipmentData.pumpChartSet.length > 0) ||
    (crossSectionData.value.topEquipmentData.intakeChartSet &&
      crossSectionData.value.topEquipmentData.intakeChartSet.length > 0) ||
    (crossSectionData.value.topEquipmentData.sealChartSet &&
      crossSectionData.value.topEquipmentData.sealChartSet.length > 0) ||
    crossSectionData.value.topEquipmentData.sensorChart ||
    crossSectionData.value.topEquipmentData.motorChart ||
    crossSectionData.value.topEquipmentData.boltOnDischargeChart
  );
});

const hasBottomEquipmentData = computed(() => {
  if (!crossSectionData.value?.bottomEquipmentData) return false;
  return !!(
    (crossSectionData.value.bottomEquipmentData.tubingChartSet &&
      crossSectionData.value.bottomEquipmentData.tubingChartSet.length > 0) ||
    (crossSectionData.value.bottomEquipmentData.pumpChartSet &&
      crossSectionData.value.bottomEquipmentData.pumpChartSet.length > 0) ||
    (crossSectionData.value.bottomEquipmentData.intakeChartSet &&
      crossSectionData.value.bottomEquipmentData.intakeChartSet.length > 0) ||
    (crossSectionData.value.bottomEquipmentData.sealChartSet &&
      crossSectionData.value.bottomEquipmentData.sealChartSet.length > 0) ||
    crossSectionData.value.bottomEquipmentData.sensorChart ||
    crossSectionData.value.bottomEquipmentData.motorChart ||
    crossSectionData.value.bottomEquipmentData.boltOnDischargeChart
  );
});
</script>

<style lang="scss" scoped>
.cross-section-charts-wrapper {
  width: 100%;
}

.display-controls {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-bottom: 1rem;
}
</style>
